export const isValidHex = hex => {
  return /^#([0-9A-F]{3}){1,2}$/i.test(hex);
};

export const isValidRgb = rgb => {
  return /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i.test(rgb);
};

export const isValidRgba = rgba => {
  return /^rgba\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*([\d.]+)\s*\)$/i.test(rgba);
};

export const hexToRgb = hex => {
  hex = hex.replace('#', '');

  if (hex.length === 3) {
    hex = hex
      .split('')
      .map(char => char + char)
      .join('');
  }

  const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

export const rgbToHex = (r, g, b) => {
  return (
    '#' +
    ((1 << 24) + (r << 16) + (g << 8) + b)
      .toString(16)
      .slice(1)
      .toUpperCase()
  );
};

export const hexToRgba = hex => {
  hex = hex.replace('#', '');

  // validate hex format
  if (!/^[0-9A-Fa-f]{6}$/.test(hex)) {
    return null;
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return { r, g, b, a: 1.0 };
};

export const convertHexToRgbaString = hex => {
  const result = hexToRgba(hex);
  if (!result) {
    return null;
  }

  const { r, g, b, a } = result;

  // sanitize RGB values to ensure they're valid (should always be 0-255 from hex, but safety check)
  const sanitizedR = Math.max(0, Math.min(255, r || 0));
  const sanitizedG = Math.max(0, Math.min(255, g || 0));
  const sanitizedB = Math.max(0, Math.min(255, b || 0));

  return `rgba(${sanitizedR}, ${sanitizedG}, ${sanitizedB}, ${a})`;
};

/**
 * Accepts 'rgb(r,g,b)' or 'rgba(r,g,b,a)' or hex
 * @param {string} color - The color string to convert, in either RGB or Hex format
 * @returns {string} - Returns a string in the format 'rgba(r,g,b,1.0)' if valid, otherwise returns 'rgba(255,255,255,1.0)'.
 */
export const convertRGBOrHexToRGBAWithOneAlpha = color => {
  if (!color || typeof color !== 'string') return 'rgba(255,255,255,1.0)';
  let val = color.trim();

  if (val.startsWith('#')) {
    // hex to rgba
    let hex = val.replace('#', '');
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map(x => x + x)
        .join('');
    }
    const num = parseInt(hex, 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    return `rgba(${r},${g},${b},1.0)`;
  }

  if (val.startsWith('rgb(')) {
    // rgb to rgba
    const rgb = val.match(/\d+/g);
    if (rgb && rgb.length === 3) {
      return `rgba(${rgb[0]},${rgb[1]},${rgb[2]},1.0)`;
    }
  }

  if (val.startsWith('rgba(')) {
    // replace alpha with 1
    const rgba = val.match(/\d+(\.\d+)?/g);
    if (rgba && rgba.length >= 3) {
      return `rgba(${rgba[0]},${rgba[1]},${rgba[2]},1.0)`;
    }
  }

  return 'rgba(255,255,255,1.0)';
};

export const formatColorValue = (r, g, b, format = 'rgba') => {
  if (format === 'hex') {
    return rgbToHex(r, g, b);
  } else {
    return `rgb(${r}, ${g}, ${b})`;
  }
};

export const formatColorValueWithAlpha = (r, g, b, a = 1, format = 'rgba') => {
  if (format === 'rgba') {
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  } else if (format === 'hex' && a === 1) {
    return rgbToHex(r, g, b);
  } else if (format === 'rgb' && a === 1) {
    return `rgb(${r}, ${g}, ${b})`;
  } else {
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }
};

/**
 * Parses a color value string and returns an object with the corresponding RGBA values.
 * @param {string} colorValue - The color value string to parse - hex, rgb, or rgba.
 * @returns {Object|null} - An object with r, g, b, and a properties, or null if invalid.
 */
export const parseAnyColorValueToRgba = colorValue => {
  if (!colorValue) return null;

  if (isValidHex(colorValue)) {
    return hexToRgb(colorValue);
  } else if (isValidRgb(colorValue)) {
    const match = colorValue.match(/^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i);
    return match
      ? {
          r: parseInt(match[1]),
          g: parseInt(match[2]),
          b: parseInt(match[3]),
        }
      : null;
  } else if (isValidRgba(colorValue)) {
    const match = colorValue.match(
      /^rgba\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*([\d.]+)\s*\)$/i
    );
    return match
      ? {
          r: parseInt(match[1]),
          g: parseInt(match[2]),
          b: parseInt(match[3]),
          a: parseFloat(match[4]),
        }
      : null;
  }

  return null;
};

export const validateColorValue = value => {
  if (!value || value.trim() === '') {
    return 'Color value is required';
  }

  if (!isValidHex(value) && !isValidRgb(value) && !isValidRgba(value)) {
    return 'Please enter a valid color in Hex (#RRGGBB), RGB (rgb(r, g, b)), or RGBA (rgba(r, g, b, a)) format';
  }

  return true;
};

export const validateColorValueNoAlpha = value => {
  if (!value || value.trim() === '') {
    return 'Color value is required';
  }

  if (!isValidHex(value) && !isValidRgb(value) && !isValidRgba(value)) {
    return 'Please enter a valid color in Hex (#RRGGBB) or RGB (rgb(r, g, b)) format';
  }

  return true;
};

/**
 * Helper function to determine if text should be white or black based on background color
 * @param {string} backgroundColor - The background color in hex, rgb, or rgba format
 * @returns {string} - Returns 'rgba(255,255,255,1)' for white or 'rgba(0,0,0,1)' for black
 */
export const getContrastingTextColor = backgroundColor => {
  if (!backgroundColor) return 'rgba(255,255,255,1)';

  let colorObj = null;

  if (isValidHex(backgroundColor)) {
    colorObj = hexToRgb(backgroundColor);
  } else if (isValidRgb(backgroundColor)) {
    colorObj = parseAnyColorValueToRgba(backgroundColor);
  } else if (isValidRgba(backgroundColor)) {
    colorObj = parseAnyColorValueToRgba(backgroundColor);
  }

  if (
    !colorObj ||
    typeof colorObj.r !== 'number' ||
    typeof colorObj.g !== 'number' ||
    typeof colorObj.b !== 'number'
  ) {
    return 'rgba(255,255,255,1)';
  }

  const { r, g, b } = colorObj;
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? 'rgba(0,0,0,1)' : 'rgba(255,255,255,1)';
};

/**
 * Converts RGB values to HSB (Hue, Saturation, Brightness)
 * @param {number} r - Red value (0-255)
 * @param {number} g - Green value (0-255)
 * @param {number} b - Blue value (0-255)
 * @returns {Object} - Object with h, s, b properties
 */
export const rgbToHsb = (r, g, b) => {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  let s = max === 0 ? 0 : delta / max;
  let brightness = max;

  if (delta !== 0) {
    if (max === r) {
      h = ((g - b) / delta) % 6;
    } else if (max === g) {
      h = (b - r) / delta + 2;
    } else {
      h = (r - g) / delta + 4;
    }
    h /= 6;
  }

  if (h < 0) h += 1;

  return { h, s, b: brightness };
};

/**
 * Converts HSB values to RGB
 * @param {number} h - Hue (0-1)
 * @param {number} s - Saturation (0-1)
 * @param {number} b - Brightness (0-1)
 * @returns {Object} - Object with r, g, b properties (0-255)
 */
export const hsbToRgb = (h, s, b) => {
  const c = b * s;
  const x = c * (1 - Math.abs(((h * 6) % 2) - 1));
  const m = b - c;

  let r = 0, g = 0, blue = 0;

  if (h >= 0 && h < 1/6) {
    r = c; g = x; blue = 0;
  } else if (h >= 1/6 && h < 2/6) {
    r = x; g = c; blue = 0;
  } else if (h >= 2/6 && h < 3/6) {
    r = 0; g = c; blue = x;
  } else if (h >= 3/6 && h < 4/6) {
    r = 0; g = x; blue = c;
  } else if (h >= 4/6 && h < 5/6) {
    r = x; g = 0; blue = c;
  } else if (h >= 5/6 && h < 1) {
    r = c; g = 0; blue = x;
  }

  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((blue + m) * 255)
  };
};

/**
 * Generates a lighter shade (tint) of a given color, similar to the backend logic
 * @param {string} primaryColorStr - The primary color in hex, rgb, or rgba format
 * @returns {string} - A lighter secondary color in rgba format
 */
export const estimateSecondaryColor = (primaryColorStr) => {
  if (!primaryColorStr) {
    return 'rgba(255, 255, 255, 1.0)';
  }

  const colorObj = parseAnyColorValueToRgba(primaryColorStr);
  if (!colorObj) {
    return 'rgba(255, 255, 255, 1.0)';
  }

  const { r, g, b, a } = colorObj;

  // Convert to HSB (Hue, Saturation, Brightness)
  const hsb = rgbToHsb(r, g, b);

  // For very low saturation (gray-ish colors), keep them gray by setting saturation to 0
  // Prevents gray colors from becoming pinkish due to default hue of 0 (red)
  const isGrayish = hsb.s < 0.1;
  const newSaturation = isGrayish ? 0 : 0.07; 
  const newBrightness = .98; 
  const newRgb = hsbToRgb(hsb.h, newSaturation, newBrightness);

  // detect input format and match it in output
  const trimmedInput = primaryColorStr.trim();
  
  if (isValidHex(trimmedInput)) {
    return rgbToHex(newRgb.r, newRgb.g, newRgb.b);
  } else if (isValidRgb(trimmedInput)) {
    return `rgb(${newRgb.r}, ${newRgb.g}, ${newRgb.b})`;
  } else {
    return `rgba(${newRgb.r}, ${newRgb.g}, ${newRgb.b}, ${a || 1.0})`;
  }
};

/**
 * Adjusts a hex color's lightness by a percentage
 * @param {string} hex - Hex color (e.g., '#2196F3')
 * @param {number} percent - Percentage to lighten (positive) or darken (negative)
 * @returns {string} Modified hex color
 * 
 * @example
 * adjustColorLightness('#2196F3', 20) => '#6EC1FF' (lighter)
 * adjustColorLightness('#2196F3', -20) => '#0B79D0' (darker)
 */
export const adjustColorLightness = (hex, percent) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const adjust = (channel) => {
    const adjusted = channel + Math.round((percent / 100) * 255);
    return Math.max(0, Math.min(255, adjusted));
  };

  const newR = adjust(rgb.r);
  const newG = adjust(rgb.g);
  const newB = adjust(rgb.b);

  return rgbToHex(newR, newG, newB);
};

/**
 * Interpolate between two hex colors, returning a hex color at the given ratio
 * @param {string} color1 - Starting hex color
 * @param {string} color2 - Ending hex color
 * @param {number} ratio - Ratio between 0 and 1 (0 = color1, 1 = color2)
 * @returns {string} Interpolated hex color
 * 
 * @example
 * interpolateColor('#FF0000', '#0000FF', 0.5) => '#800080' (purple)
 * interpolateColor('#00FF00', '#0000FF', 0.25) => '#00BF3F' (greenish-blue)
 */
export const interpolateColor = (color1, color2, ratio) => {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);
  
  if (!rgb1 || !rgb2) return color1;
  
  const r = Math.round(rgb1.r + (rgb2.r - rgb1.r) * ratio);
  const g = Math.round(rgb1.g + (rgb2.g - rgb1.g) * ratio);
  const b = Math.round(rgb1.b + (rgb2.b - rgb1.b) * ratio);
  
  return rgbToHex(r, g, b);
};
